import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const CartContext = createContext(null);
const STORAGE_KEY = 'servispro_cart_v1';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
    catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const sameLine = (a, b) =>
    a.product_id === b.product_id && (a.variant_id || null) === (b.variant_id || null);

  const addItem = useCallback((product, quantity = 1, variant = {}) => {
    setItems(prev => {
      const cand = {
        product_id: product.id,
        variant_id: variant.variant_id || null,
      };
      const idx = prev.findIndex(it => sameLine(it, cand));
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
        return next;
      }
      return [...prev, {
        product_id: product.id,
        name: product.name,
        price: variant.price_override != null ? Number(variant.price_override) : (product.price || 0),
        currency: product.currency || 'GNF',
        photo: product.photos?.[0] || null,
        shop_id: product.shop_id,
        shop_name: product.shop?.name || product.shop_name || '',
        shop_phone: product.shop?.contact_phone || null,
        quantity,
        // Variant fields (may be null for products without variants)
        variant_id: variant.variant_id || null,
        variant_sku: variant.variant_sku || null,
        variant_attributes: variant.variant_attributes || null,
      }];
    });
  }, []);

  const removeItem = useCallback((product_id, variant_id = null) => {
    setItems(prev => prev.filter(it => !sameLine(it, { product_id, variant_id })));
  }, []);

  const updateQuantity = useCallback((product_id, quantity, variant_id = null) => {
    setItems(prev => prev.map(it =>
      sameLine(it, { product_id, variant_id }) ? { ...it, quantity: Math.max(0, quantity) } : it
    ));
  }, []);

  const refreshPrices = useCallback((priceMap) => {
    // priceMap: { [variant_id]: newPrice } — apply to any line whose variant_id matches
    setItems(prev => prev.map(it => {
      if (!it.variant_id) return it;
      const fresh = priceMap[it.variant_id];
      if (fresh == null) return it;
      const asNum = Number(fresh);
      if (Number.isNaN(asNum) || asNum === it.price) return it;
      return { ...it, price: asNum };
    }));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalItems = items.reduce((sum, it) => sum + (it.quantity > 0 ? it.quantity : 0), 0);
  const totalValue = items.reduce((sum, it) => sum + (it.price * it.quantity), 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, refreshPrices, totalItems, totalValue }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
